<template>
  <WdsMenuOption v-if="asMenuOption" @click.stop="toggleShow">
    <WdsIcon :size="16" name="edit" />
    {{ $t('Edit') }}
  </WdsMenuOption>
  <WdsButtonText v-else @click="toggleShow">
    {{ $t('Edit') }}
  </WdsButtonText>
  <Teleport to="#modals">
    <CompanyRecruiterModal
      v-if="modalReady"
      :show="show"
      :on-cancel="toggleShow"
      :on-confirm="onConfirm"
      :title="$t('Edit recruiter')"
      :name-disabled="true"
      :email-disabled="true"
      :message-disabled="true"
      :initial-name="name"
      :initial-email="email"
      :initial-fee="recruiterFee"
      :initial-ownership="recruiterOwnershipDays"
      :initial-ownership-never-expires="recruiterOwnershipDays === null"
      :initial-max-candidate-submissions="maxCandidateSubmissions"
      :initial-tags="tags"
      :initial-manages-workers="managesWorkers"
      :manage-workers="managesWorkers"
      :can-configure-recruiter-manages-workers="canConfigureRecruiterManagesWorkers"
      :has-hires="hasHires"
      :initial-custom-field-values="initialCustomFieldValues"
      :recruiter-custom-field-values="recruiterReadOnlyCustomFieldValues"
      :error-text="$t('An error occurred while trying to edit recruiter')"
    ></CompanyRecruiterModal>
  </Teleport>
</template>

<script>
import { WdsIcon, WdsButtonText, WdsMenuOption } from '@worksome/wds-components'
import CompanyRecruiterModal from '@/components/Contacts/CompanyRecruiter/CompanyRecruiterModal.vue'
import { UpdateCompanyRecruiterMutation } from '@/components/Contacts/CompanyRecruiter/mutations/UpdateCompanyRecruiterMutation'
import { EditCompanyRecruiterCustomFieldValuesQuery } from '@/components/Contacts/CompanyRecruiter/queries/EditCompanyRecruiterCustomFieldValuesQuery'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { useFragment } from '@/gql'
import { CUSTOM_FIELD_VALUES_VIEW_FRAGMENT } from '@/components/Fieldable/graphql/fragments'

export default {
  components: { CompanyRecruiterModal, WdsButtonText, WdsMenuOption, WdsIcon },
  props: {
    companyRecruiterId: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    asMenuOption: {
      type: Boolean,
      default: false,
    },
    email: {
      type: String,
      required: true,
    },
    recruiterFee: {
      type: Number,
      default: null,
    },
    maxCandidateSubmissions: {
      type: Number,
      default: null,
    },
    recruiterOwnershipDays: {
      type: Number,
      default: null,
    },
    tags: {
      type: Array,
      default: () => [],
    },
    managesWorkers: {
      type: Boolean,
      default: false,
    },
    canConfigureRecruiterManagesWorkers: {
      type: Boolean,
      default: false,
    },
    hasHires: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updated'],
  data() {
    return {
      show: false,
      companyRecruiterCustomFieldValues: null,
    }
  },
  apollo: {
    companyRecruiterCustomFieldValues: {
      query: EditCompanyRecruiterCustomFieldValuesQuery,
      client: 'publicApi',
      variables() {
        return { companyRecruiterId: this.companyRecruiterId }
      },
      skip() {
        return !this.show
      },
      update(data) {
        return data?.companyRecruiter?.customFieldValues ?? []
      },
    },
  },
  computed: {
    modalReady() {
      return Array.isArray(this.companyRecruiterCustomFieldValues)
    },
    initialCustomFieldValues() {
      return this.companyRecruiterCustomFieldValues ?? []
    },
    recruiterReadOnlyCustomFieldValues() {
      const filtered = (this.companyRecruiterCustomFieldValues ?? [])
        .filter((fv) => {
          const unwrapped = useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, fv)
          return unwrapped.customField.recruiterInputAllowed
        })
        .map((fv) => {
          const unwrapped = useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, fv)
          return {
            fieldId: unwrapped.customField.id,
            title: unwrapped.customField.title,
            displayValue: unwrapped.displayValue,
          }
        })

      const grouped = new Map()
      for (const fv of filtered) {
        if (grouped.has(fv.fieldId)) {
          const existing = grouped.get(fv.fieldId)
          existing.displayValue = [existing.displayValue, fv.displayValue]
            .filter(Boolean)
            .join(', ')
        } else {
          grouped.set(fv.fieldId, { title: fv.title, displayValue: fv.displayValue ?? '' })
        }
      }
      return Array.from(grouped.values())
    },
  },
  methods: {
    toggleShow() {
      this.show = !this.show
    },
    onConfirm(data) {
      // The modal only reports changed custom field values. When none changed,
      // leave the key out so the mutation cannot touch stored values at all.
      const changedCustomFieldValues = data.customFieldValues ?? []

      const editCompanyRecruiterMutation = {
        mutation: UpdateCompanyRecruiterMutation,
        variables: {
          input: {
            id: this.companyRecruiterId,
            recruiterFee: data.recruiterFee,
            recruiterOwnershipDays: data.recruiterOwnershipDays,
            maxCandidateSubmissions: data.maxCandidateSubmissions,
            tags: data.tags,
            ...(changedCustomFieldValues.length > 0
              ? { customFieldValues: changedCustomFieldValues }
              : {}),
            ...(data.managesWorkers !== undefined && { managesWorkers: data.managesWorkers }),
          },
        },
        clientId: 'publicApi',
      }
      return publicApi
        .mutate(editCompanyRecruiterMutation)
        .then(async (result) => {
          this.$emit('updated')
          // Refresh saved values so a subsequent open of the modal reflects the latest data
          // (the list/table refetch on `updated` does not include customFieldValues).
          await this.$apollo.queries.companyRecruiterCustomFieldValues?.refetch()
          return result
        })
        .catch((error) => {
          throw error
        })
    },
  },
}
</script>
